import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { initFcm } from "../lib/fcm";
import { toast } from "sonner";

/**
 * Login-time notification opt-in — shown every sign-in session until the user
 * grants permission. A real button click gives browsers the user gesture they
 * need to show the native permission dialog.
 */
export default function NotificationPrompt({ user, backendUrl }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "Notification" in window;
  const denied = supported && Notification.permission === "denied";

  useEffect(() => {
    if (!user || !supported) return;
    if (Notification.permission === "granted") return;
    const key = `icaard_notif_prompt_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, [user, supported]);

  // Auto-hide after 12s so the prompt never permanently blocks content near the bottom nav.
  useEffect(() => {
    if (!show || !user) return;
    const t = setTimeout(() => {
      sessionStorage.setItem(`icaard_notif_prompt_${user.id}`, "1");
      setShow(false);
    }, 12000);
    return () => clearTimeout(t);
  }, [show, user]);

  if (!show || !user) return null;

  const dismiss = () => {
    sessionStorage.setItem(`icaard_notif_prompt_${user.id}`, "1");
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await initFcm({ apiBase: backendUrl, getAuthToken: () => localStorage.getItem("icard_token") });
      if (r?.token && r.pushReady === false) toast.info("This device is registered. iCaard server push delivery still needs setup.");
      else if (r?.token) toast.success("Notifications enabled 🎉");
      else if (r?.reason === "denied") toast.error("Notifications are blocked. enable them in your browser settings.");
      else toast.info("Could not enable notifications on this device.");
    } catch (_) {
      toast.error("Could not enable notifications.");
    }
    setBusy(false);
    dismiss();
  };

  return (
    <div className="fixed inset-x-3 bottom-[calc(6.5rem+env(safe-area-inset-bottom))] sm:bottom-6 sm:right-6 sm:left-auto sm:max-w-sm z-30 pointer-events-none" data-testid="notification-prompt">
      <div className="pointer-events-auto rounded-2xl border border-[#6D28FF]/50 bg-[#13131E]/95 backdrop-blur-xl shadow-2xl shadow-black/60 p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex w-10 h-10 rounded-full bg-[#6D28FF]/20 text-[#A78BFA] items-center justify-center flex-shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white flex items-center gap-1.5"><Bell size={13} className="sm:hidden text-[#A78BFA]" /> Turn on notifications</div>
            <div className="hidden sm:block text-xs text-[#9CA3D4] mt-0.5">
              {denied
                ? "Notifications are blocked for iCaard. Enable them in your browser settings to get payment alerts, ticket updates and wins."
                : "Get instant alerts for payments, tickets, bookings and Spin-to-Win prizes."}
            </div>
            <div className="mt-2 sm:mt-3 flex items-center gap-2">
              {!denied && (
                <button data-testid="notif-allow-btn" onClick={enable} disabled={busy}
                        className="px-4 py-2 bg-[#6D28FF] hover:bg-[#7C3AED] disabled:opacity-60 text-white text-xs font-bold rounded-lg">
                  {busy ? "Enabling…" : "Allow notifications"}
                </button>
              )}
              <button data-testid="notif-later-btn" onClick={dismiss}
                      className="px-4 py-2 border border-[#26263A] hover:bg-[#1A1A27] text-[#9CA3D4] text-xs font-bold rounded-lg">
                {denied ? "Got it" : "Not now"}
              </button>
            </div>
          </div>
          <button onClick={dismiss} className="text-[#6F7285] hover:text-white flex-shrink-0" aria-label="Dismiss" data-testid="notif-dismiss-btn">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
